import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Textarea,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Switch,
  Select,
  Tag,
  Badge,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { FiTrash2, FiPlus, FiEdit2, FiSearch } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  Timestamp,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

// Cast icons as React.ElementType to fix TypeScript issues
const FiTrash2Icon = FiTrash2 as React.ElementType;
const FiPlusIcon = FiPlus as React.ElementType;
const FiEdit2Icon = FiEdit2 as React.ElementType;
const FiSearchIcon = FiSearch as React.ElementType;

export interface FAQ {
  id?: string;
  question: string;
  answer: string;
  category: string;
  keywords: string[];
  order: number;
  isActive: boolean;
  createdAt?: any;
  updatedAt?: any;
}

const FAQ_CATEGORIES = [
  'services_pricing',
  'booking_scheduling',
  'preparing_appointment',
  'weather_policies',
  'cancellation_refunds',
  'service_area',
  'gift_cards',
  'booking_pools',
  'promo_codes',
  'payment',
  'technician_info',
  'general'
];

const FAQ_CATEGORY_LABELS: Record<string, string> = {
  'services_pricing': 'Services & Pricing',
  'booking_scheduling': 'Booking & Scheduling',
  'preparing_appointment': 'Preparing for Appointment',
  'weather_policies': 'Weather Policies',
  'cancellation_refunds': 'Cancellation & Refunds',
  'service_area': 'Service Area',
  'gift_cards': 'Gift Cards',
  'booking_pools': 'Booking Pools',
  'promo_codes': 'Promo Codes',
  'payment': 'Payment',
  'technician_info': 'Technician Info',
  'general': 'General'
};

const FAQsManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [faqForm, setFaqForm] = useState<FAQ>({
    question: '',
    answer: '',
    category: 'general',
    keywords: [],
    order: 0,
    isActive: true,
  });

  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    loadFAQs();
  }, []);

  useEffect(() => {
    filterFAQs();
  }, [faqs, searchTerm, categoryFilter]);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      const faqsRef = collection(db, 'faqs');
      const q = query(faqsRef, orderBy('order', 'asc'), orderBy('category', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const loadedFaqs: FAQ[] = [];
      querySnapshot.forEach((doc) => {
        loadedFaqs.push({
          id: doc.id,
          ...doc.data()
        } as FAQ);
      });
      
      setFaqs(loadedFaqs);
    } catch (error) {
      console.error('Error loading FAQs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load FAQs',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterFAQs = () => {
    let filtered = [...faqs];

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(faq => faq.category === categoryFilter);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(searchLower) ||
        faq.answer.toLowerCase().includes(searchLower) ||
        faq.keywords.some(kw => kw.toLowerCase().includes(searchLower))
      );
    }

    setFilteredFaqs(filtered);
  };

  const handleAddFAQ = () => {
    setEditingFaq(null);
    setFaqForm({
      question: '',
      answer: '',
      category: 'general',
      keywords: [],
      order: faqs.length > 0 ? Math.max(...faqs.map(f => f.order || 0)) + 1 : 0,
      isActive: true,
    });
    setKeywordInput('');
    onOpen();
  };

  const handleEditFAQ = (faq: FAQ) => {
    setEditingFaq(faq);
    setFaqForm({ ...faq });
    setKeywordInput(faq.keywords.join(', '));
    onOpen();
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim()) {
      const keywords = keywordInput.split(',').map(k => k.trim()).filter(k => k.length > 0);
      setFaqForm(prev => {
        const combined = [...prev.keywords, ...keywords];
        const unique = Array.from(new Set(combined)); // Remove duplicates
        return {
          ...prev,
          keywords: unique
        };
      });
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFaqForm(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const handleSaveFAQ = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in question and answer',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const faqData = {
        question: faqForm.question.trim(),
        answer: faqForm.answer.trim(),
        category: faqForm.category,
        keywords: faqForm.keywords,
        order: faqForm.order || 0,
        isActive: faqForm.isActive,
        updatedAt: Timestamp.now(),
      };

      if (editingFaq && editingFaq.id) {
        // Update existing FAQ
        const faqRef = doc(db, 'faqs', editingFaq.id);
        await updateDoc(faqRef, faqData);
        toast({
          title: 'Success',
          description: 'FAQ updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new FAQ
        const newFaqData = {
          ...faqData,
          createdAt: Timestamp.now(),
        };
        await addDoc(collection(db, 'faqs'), newFaqData);
        toast({
          title: 'Success',
          description: 'FAQ created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      onClose();
      await loadFAQs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast({
        title: 'Error',
        description: 'Failed to save FAQ',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFAQ = async (faqId: string) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'faqs', faqId));
      toast({
        title: 'Success',
        description: 'FAQ deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await loadFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete FAQ',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleActive = async (faq: FAQ) => {
    if (!faq.id) return;

    try {
      const faqRef = doc(db, 'faqs', faq.id);
      await updateDoc(faqRef, {
        isActive: !faq.isActive,
        updatedAt: Timestamp.now(),
      });
      await loadFAQs();
    } catch (error) {
      console.error('Error toggling FAQ status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update FAQ status',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return <Text>Loading FAQs...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          mb={4}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
        >
          FAQs Management
        </Heading>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Manage frequently asked questions that help customers and are used by the Nibi AI assistant.
              FAQs are searchable by keywords and organized by category.
            </Text>
          </Box>
        </Alert>
      </Box>

      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4}>
              <InputGroup flex="1">
                <InputLeftElement pointerEvents="none">
                  <FiSearchIcon />
                </InputLeftElement>
                <Input
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Select
                width="200px"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                {FAQ_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>
                    {FAQ_CATEGORY_LABELS[cat]}
                  </option>
                ))}
              </Select>
              <Button
                leftIcon={<FiPlusIcon />}
                colorScheme="blue"
                onClick={handleAddFAQ}
              >
                Add FAQ
              </Button>
            </HStack>

            {filteredFaqs.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                {faqs.length === 0 
                  ? 'No FAQs found. Click "Add FAQ" to create one.'
                  : 'No FAQs match your search criteria.'}
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Order</Th>
                      <Th>Question</Th>
                      <Th>Category</Th>
                      <Th>Keywords</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredFaqs.map((faq) => (
                      <Tr key={faq.id}>
                        <Td>{faq.order || 0}</Td>
                        <Td fontWeight="medium" maxW="300px">
                          <Text noOfLines={2}>{faq.question}</Text>
                        </Td>
                        <Td>
                          <Badge colorScheme="blue">
                            {FAQ_CATEGORY_LABELS[faq.category] || faq.category}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={1} flexWrap="wrap">
                            {faq.keywords.slice(0, 3).map((kw, idx) => (
                              <Tag key={idx} size="sm">{kw}</Tag>
                            ))}
                            {faq.keywords.length > 3 && (
                              <Tag size="sm">+{faq.keywords.length - 3}</Tag>
                            )}
                          </HStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={faq.isActive ? 'green' : 'gray'}>
                            {faq.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Edit"
                              icon={<FiEdit2Icon />}
                              size="sm"
                              colorScheme="blue"
                              onClick={() => handleEditFAQ(faq)}
                            />
                            <IconButton
                              aria-label="Toggle Active"
                              size="sm"
                              colorScheme={faq.isActive ? 'orange' : 'green'}
                              onClick={() => handleToggleActive(faq)}
                              title={faq.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {faq.isActive ? '✓' : '○'}
                            </IconButton>
                            <IconButton
                              aria-label="Delete"
                              icon={<FiTrash2Icon />}
                              size="sm"
                              colorScheme="red"
                              onClick={() => faq.id && handleDeleteFAQ(faq.id)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Add/Edit FAQ Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingFaq ? 'Edit FAQ' : 'Add FAQ'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Question</FormLabel>
                <Input
                  value={faqForm.question}
                  onChange={(e) => setFaqForm(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="e.g., How do I book a car wash?"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Answer</FormLabel>
                <Textarea
                  value={faqForm.answer}
                  onChange={(e) => setFaqForm(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder="Provide a clear, helpful answer..."
                  rows={6}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  value={faqForm.category}
                  onChange={(e) => setFaqForm(prev => ({ ...prev, category: e.target.value }))}
                >
                  {FAQ_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>
                      {FAQ_CATEGORY_LABELS[cat]}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Keywords (comma-separated)</FormLabel>
                <HStack>
                  <Input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    placeholder="e.g., booking, schedule, appointment"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddKeyword();
                      }
                    }}
                  />
                  <Button onClick={handleAddKeyword}>Add</Button>
                </HStack>
                <HStack spacing={2} mt={2} flexWrap="wrap">
                  {faqForm.keywords.map((kw, idx) => (
                    <Tag key={idx} size="md" colorScheme="blue">
                      {kw}
                      <IconButton
                        aria-label="Remove keyword"
                        icon={<FiTrash2Icon />}
                        size="xs"
                        ml={2}
                        onClick={() => handleRemoveKeyword(kw)}
                        variant="ghost"
                      />
                    </Tag>
                  ))}
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel>Order (for sorting)</FormLabel>
                <Input
                  type="number"
                  value={faqForm.order}
                  onChange={(e) => setFaqForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} flex="1">
                  Active
                </FormLabel>
                <Switch
                  isChecked={faqForm.isActive}
                  onChange={(e) => setFaqForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSaveFAQ}
              isLoading={saving}
              loadingText="Saving"
            >
              {editingFaq ? 'Update' : 'Create'} FAQ
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FAQsManagement;

